/**
 * React Query mutation hooks for video operations
 * Wraps video mutations (generate, upload, update) with React Query
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  generateVideo,
  uploadVideo,
  updateVideoMetadata,
  type VideoGenerationRequest,
} from '@/api/videoApi';
import videoKeys from '@/api/querry_keys/videoKeys';
import {
  updateItemInData,
  cancelQueriesForResource,
  getQueriesSnapshot,
  applyOptimisticUpdate,
  rollbackQueries,
} from './shared-mutation-utils';

/**
 * Interface for video update parameters
 */
interface UpdateVideoParams {
  videoId: string;
  status?: string;
  feedback?: string;
  title?: string;
  delete_request?: boolean;
  previousStatus?: string;
  userEmail?: string;
  adminEmail?: string;
}

/**
 * Hook for updating video metadata with optimistic updates
 */
export const useUpdateVideoMetadata = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ videoId, status, feedback, title, delete_request }: UpdateVideoParams) => {
      return await updateVideoMetadata(videoId, status, feedback?.trim() || undefined, delete_request, title?.trim());
    },
    onMutate: async ({ videoId, status, feedback, title, delete_request, previousStatus }: UpdateVideoParams) => {
      // Cancel outgoing refetches for ALL video-related queries
      await cancelQueriesForResource(queryClient, 'videos');
      
      // Snapshot previous value for ALL video queries
      const previousData = getQueriesSnapshot(queryClient, 'videos');
      
      // Optimistically update ALL video queries (including infinite scroll, lists, etc.)
      // Only include defined fields to avoid overwriting existing data with undefined
      const updates: Record<string, any> = {};
      if (status !== undefined) updates.status = status;
      if (feedback !== undefined) updates.feedback = feedback;
      if (title !== undefined) updates.title = title;
      if (delete_request !== undefined) updates.delete_request = delete_request;
      
      const updater = updateItemInData(videoId, updates);
      applyOptimisticUpdate(queryClient, 'videos', updater);
      
      // Return context for rollback
      return { previousData, previousStatus };
    },
    onError: (_, __, context) => {
      // Rollback to the previous values
      if (context?.previousData) {
        rollbackQueries(queryClient, context.previousData);
      }
    },
    onSuccess: () => {
      // Invalidate ALL video queries to trigger refetch and ensure consistency
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          return query.queryKey.length > 0 && query.queryKey[0] === 'videos';
        },
        refetchType: 'none' // Don't refetch, just mark as stale
      });
    },
    onSettled: () => {
      // Final consistency check
    },
  });
};

/**
 * Hook for generating a video
 */
export const useGenerateVideo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (request: VideoGenerationRequest) => {
      return await generateVideo(request);
    },
    onSuccess: () => {
      // Invalidate video lists to show the new generating video
      queryClient.invalidateQueries({ queryKey: videoKeys.lists() });
      queryClient.invalidateQueries({ queryKey: videoKeys.generations() });
    },
  });
};

/**
 * Hook for uploading a video
 */
interface UploadVideoParams {
  video: File;
  prompt: string;
  model: string;
}

export const useUploadVideo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ video, prompt, model }: UploadVideoParams) => {
      return await uploadVideo(video, prompt, model);
    },
    onSuccess: () => {
      // Invalidate video lists to show the new uploaded video
      queryClient.invalidateQueries({ queryKey: videoKeys.lists() });
    },
  });
};

/**
 * Combined hook for all video mutations
 * Provides easy access to all video mutation operations
 */
export const useVideoMutations = () => {
  const generateMutation = useGenerateVideo();
  const uploadMutation = useUploadVideo();

  return {
    // Mutation functions
    generateVideo: generateMutation.mutate,
    generateVideoAsync: generateMutation.mutateAsync,
    uploadVideo: uploadMutation.mutate,
    uploadVideoAsync: uploadMutation.mutateAsync,

    // Loading states
    isGenerating: generateMutation.isPending,
    isUploading: uploadMutation.isPending,

    // Error states
    generateError: generateMutation.error,
    uploadError: uploadMutation.error,

    // Success states
    generateSuccess: generateMutation.isSuccess,
    uploadSuccess: uploadMutation.isSuccess,

    // Reset functions
    resetGenerate: generateMutation.reset,
    resetUpload: uploadMutation.reset,

    // Raw mutation objects for advanced use
    generateMutation,
    uploadMutation,
  };
};
